import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Check, Crop, Maximize2, RotateCw, ScanFace, ScanLine, Wand2 } from 'lucide-react';
import type { MediaSummary } from '@familienbaum/shared';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/misc';
import { PhotoFrame } from '@/components/ui/paper';
import { ScanFaces } from '@/components/media/scan-faces';
import type { UploadOutcome } from '@/lib/use-media-upload';
import { detectPhotoQuad } from '@/lib/scan/detect';
import {
  clamp,
  clampQuad,
  FULL_QUAD,
  insetQuad,
  rotateQuad,
  shrinkQuad,
  type Point,
  type Quad,
} from '@/lib/scan/geometry';
import {
  canvasToBlob,
  imageDataFrom,
  loadImageFile,
  outputSize,
  scanFilename,
  SOURCE_MAX_EDGE,
  warpQuad,
} from '@/lib/scan/warp';
import { cn } from '@/lib/utils';

/**
 * Knöpfe auf der dunklen Tafel.
 *
 * `variant="primary"` geht hier nicht: im hellen Farbschema ist die
 * Markenfarbe die Tinte, und ein tintenfarbener Knopf auf einer
 * tintenfarbenen Fläche ist ein Loch. Die Hauptaktion trägt deshalb Gold –
 * genauso wie auf allen anderen dunklen Tafeln der App.
 */
const GOLD_ACTION =
  'bg-accent font-semibold text-accent-ink hover:bg-accent hover:brightness-105 active:brightness-95';
const GHOST_ON_INK = 'text-panel-ink hover:bg-panel-line hover:text-panel-ink';
/** Die Werkzeuge unter dem Bild: gefüllte Flächen, damit sie greifbar sind. */
const TOOL_ON_INK = 'bg-panel-line text-panel-ink hover:bg-panel-line hover:brightness-125';

/** Kantenlänge, auf der die Erkennung rechnet – mehr bringt nichts, kostet nur Zeit. */
const DETECT_EDGE = 480;

/**
 * Ab welcher Kantendeckung die Erkennung als eindeutig gilt.
 *
 * Nicht höher ansetzen: das Winkelraster der Erkennung ist 1° grob, und über
 * eine Bildbreite hinweg weicht die gefundene Gerade dadurch ein paar Pixel
 * von der echten Kante ab. Eine schräg abfotografierte, sauber erkannte
 * Aufnahme landet deshalb selten über 0,8 – nur achsenparallele erreichen 1,0.
 * Eine strengere Schwelle würde also fast jedes Foto vom Tisch mit einem
 * „bitte prüfen" behängen und den Hinweis damit wertlos machen.
 */
const CONFIDENT_COVERAGE = 0.75;

/**
 * Wie weit das erkannte Viereck nach innen gezogen wird, damit vom Untergrund
 * kein Streifen im Ergebnis stehen bleibt.
 */
const EDGE_TRIM = 0.006;

/** Vergrößerung der Lupe beim Ziehen einer Ecke. */
const LOUPE_ZOOM = 3;
const LOUPE_SIZE = 96;

type Phase = 'reading' | 'edit' | 'working' | 'done' | 'unreadable';
type Detection = 'pending' | 'found' | 'unsure' | 'missed';

/**
 * Einen gezeichneten Frame abwarten.
 *
 * Das Entzerren eines 5-Megapixel-Ausschnitts läuft als eine einzige Schleife
 * auf dem Hauptthread und blockiert ihn dabei je nach Gerät fast eine Sekunde.
 * Ohne diese Pause setzt React den Ladezustand am Knopf zwar, malt ihn aber
 * erst hinterher – für den Benutzer sähe es aus, als hätte sein Tippen nichts
 * bewirkt.
 */
function afterPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => window.setTimeout(resolve, 0));
  });
}

interface Fit {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** Wo das Bild innerhalb des Rahmens wirklich liegt (`object-fit: contain`). */
function fitRect(cw: number, ch: number, iw: number, ih: number): Fit {
  const scale = Math.min(cw / iw, ch / ih);
  const width = iw * scale;
  const height = ih * scale;
  return { width, height, left: (cw - width) / 2, top: (ch - height) / 2 };
}

interface Drag {
  pointerId: number;
  kind: 'corner' | 'edge';
  index: number;
  start: Point;
  startQuad: Quad;
}

/**
 * Der Scan-Schritt zwischen Kamera und Archiv.
 *
 * Ablauf: Bild kommt herein → Ecken werden automatisch gesucht → der Benutzer
 * korrigiert sie mit dem Finger → das Viereck wird perspektivisch geradegerückt
 * und als JPEG hochgeladen. Danach bleibt der Dialog offen und bietet direkt
 * die nächste Aufnahme an; ein Fotoalbum abzufotografieren ist damit eine
 * Schleife aus zwei Tipp-Vorgängen pro Seite.
 *
 * Alles rechnet im Browser. Für das Archiv heißt das: hochgeladen wird nur der
 * Zuschnitt, ohne Tisch und ohne Albumrand – und ohne dass der Server ein
 * zweites Bild speichern muss.
 *
 * Ein Nebeneffekt, der bewusst so ist: durch die Canvas gehen die EXIF-Daten
 * der Aufnahme verloren. Bei einem abfotografierten Foto ist das
 * Aufnahmedatum ohnehin das von heute und nicht das des Bildes – und genau
 * dieser Widerspruch wird im Archiv sonst als Konflikt angezeigt. Wer das
 * Original mit allen Kameradaten behalten will, lädt es über
 * „Ohne Zuschnitt hochladen" hoch.
 */
export function PhotoScanner({
  file,
  open,
  onOpenChange,
  onUpload,
  onNext,
}: {
  file: File | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Lädt die Datei hoch und sagt, was angekommen ist. Der Rückweg trägt das
   * neue Medium mit, damit direkt danach die Gesichter benannt werden können –
   * ohne den Umweg über das Fotoalbum.
   */
  onUpload: (file: File) => Promise<UploadOutcome>;
  /** Öffnet die Kamera erneut – muss aus einem Klick heraus laufen (iOS). */
  onNext?: () => void;
}) {
  const [phase, setPhase] = useState<Phase>('reading');
  const [detection, setDetection] = useState<Detection>('pending');
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [quad, setQuad] = useState<Quad>(() => insetQuad());
  const [rotation, setRotation] = useState(0);
  const [preview, setPreview] = useState<string | null>(null);
  const [loupe, setLoupe] = useState<Point | null>(null);
  const [box, setBox] = useState<{ width: number; height: number } | null>(null);
  /**
   * Das Bild, das gerade angekommen ist. Es trägt den Weg zum nächsten
   * Handgriff: markierte Gesichter benennen, solange man noch bei der Familie
   * sitzt. Ohne diesen Zustand endete der Zuschnitt bei „angekommen“ – und die
   * Gesichter blieben liegen.
   */
  const [uploaded, setUploaded] = useState<MediaSummary | null>(null);
  /**
   * Genau das Bild, das hochgeladen wurde – als geladenes `<img>`.
   *
   * Die Gesichtserkennung danach rechnet darauf, und die Ausschnitte im
   * Benennen-Schritt schneiden daraus. Die Ableitungen vom Server wären das
   * Naheliegende, entstehen aber erst im Hintergrund: bis dahin gäbe es hier
   * nichts zu sehen und nichts zu rechnen.
   *
   * Beim Zuschnitt ist es das gewarpte Ergebnis, ohne Zuschnitt die Aufnahme
   * selbst – und nicht das, was im Rahmen zu sehen war. Sonst lägen die
   * gefundenen Rahmen auf dem falschen Bild.
   */
  const [uploadedImage, setUploadedImage] = useState<HTMLImageElement | null>(null);
  const uploadedUrlRef = useRef<string | null>(null);

  const frameRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<Drag | null>(null);
  const sourceRef = useRef<ImageData | null>(null);
  const revokeRef = useRef<(() => void) | null>(null);

  // ── Bild einlesen ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!file) return;

    let cancelled = false;
    setPhase('reading');
    setDetection('pending');
    setRotation(0);
    setQuad(insetQuad());
    setPreview(null);
    setImage(null);
    setUploaded(null);
    setUploadedImage(null);
    sourceRef.current = null;

    void loadImageFile(file)
      .then(({ image: loaded, revoke }) => {
        if (cancelled) {
          revoke();
          return;
        }
        revokeRef.current?.();
        revokeRef.current = revoke;
        setImage(loaded);
        setPhase('edit');
      })
      .catch(() => {
        if (!cancelled) setPhase('unreadable');
      });

    return () => {
      cancelled = true;
    };
  }, [file]);

  // Objekt-URL freigeben, wenn der Dialog zugeht – sonst hält der Browser
  // jedes abfotografierte Bild bis zum Neuladen der Seite im Speicher.
  useEffect(() => {
    if (open) return;
    revokeRef.current?.();
    revokeRef.current = null;
    sourceRef.current = null;
    if (uploadedUrlRef.current) {
      URL.revokeObjectURL(uploadedUrlRef.current);
      uploadedUrlRef.current = null;
    }
    setUploadedImage(null);
    setUploaded(null);
  }, [open]);

  useEffect(
    () => () => {
      revokeRef.current?.();
      revokeRef.current = null;
    },
    [],
  );

  // ── Ecken automatisch suchen ─────────────────────────────────────────────
  const detect = useCallback(
    (target: HTMLImageElement) => {
      setDetection('pending');
      // Erst zeichnen lassen, dann rechnen: sonst steht der Dialog beim
      // Öffnen einen Moment leer da.
      requestAnimationFrame(() => {
        try {
          const found = detectPhotoQuad(imageDataFrom(target, DETECT_EDGE));
          if (found) {
            setQuad(clampQuad(shrinkQuad(found.quad, EDGE_TRIM)));
            setDetection(found.confidence >= CONFIDENT_COVERAGE ? 'found' : 'unsure');
          } else {
            setQuad(insetQuad());
            setDetection('missed');
          }
        } catch {
          setDetection('missed');
        }
        // Die Quelle in voller Arbeitsauflösung wird für Vorschau und Export
        // gebraucht; einmal lesen reicht.
        try {
          sourceRef.current ??= imageDataFrom(target, SOURCE_MAX_EDGE);
        } catch {
          sourceRef.current = null;
        }
      });
    },
    [],
  );

  useEffect(() => {
    if (image) detect(image);
  }, [image, detect]);

  // ── Rahmengröße verfolgen ────────────────────────────────────────────────
  useEffect(() => {
    const node = frameRef.current;
    if (!node) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setBox({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(node);
    setBox({ width: node.clientWidth, height: node.clientHeight });
    return () => observer.disconnect();
  }, [image]);

  // ── Vorschau des Ergebnisses ─────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'edit' || loupe) return;
    const handle = window.setTimeout(() => {
      const source = sourceRef.current;
      if (!source) return;
      try {
        const canvas = warpQuad(source, rotateQuad(quad, rotation), {
          maxEdge: 240,
          maxPixels: 240 * 240,
        });
        setPreview(canvas.toDataURL('image/jpeg', 0.8));
      } catch {
        setPreview(null);
      }
    }, 160);
    return () => window.clearTimeout(handle);
  }, [quad, rotation, phase, loupe, detection]);

  const fit =
    image && box && box.width > 0 && box.height > 0
      ? fitRect(box.width, box.height, image.naturalWidth, image.naturalHeight)
      : null;

  const toNormalized = (clientX: number, clientY: number): Point | null => {
    const node = frameRef.current;
    if (!node || !fit) return null;
    const rect = node.getBoundingClientRect();
    return {
      x: clamp((clientX - rect.left - fit.left) / fit.width, 0, 1),
      y: clamp((clientY - rect.top - fit.top) / fit.height, 0, 1),
    };
  };

  const beginDrag = (event: React.PointerEvent, kind: Drag['kind'], index: number) => {
    const point = toNormalized(event.clientX, event.clientY);
    if (!point) return;
    event.preventDefault();
    dragRef.current = { pointerId: event.pointerId, kind, index, start: point, startQuad: quad };
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    if (kind === 'corner') setLoupe(quad[index]!);
  };

  const moveDrag = (event: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const point = toNormalized(event.clientX, event.clientY);
    if (!point) return;

    if (drag.kind === 'corner') {
      const next = [...drag.startQuad] as Quad;
      next[drag.index] = point;
      setQuad(next);
      setLoupe(point);
      return;
    }

    // Ganze Seite verschieben: beide Endpunkte um dieselbe Strecke, aber nur
    // so weit, wie der Bildrand es für *beide* zulässt.
    const a = drag.index;
    const b = (drag.index + 1) % 4;
    let dx = point.x - drag.start.x;
    let dy = point.y - drag.start.y;
    for (const i of [a, b]) {
      const corner = drag.startQuad[i]!;
      dx = clamp(dx, -corner.x, 1 - corner.x);
      dy = clamp(dy, -corner.y, 1 - corner.y);
    }
    const next = [...drag.startQuad] as Quad;
    next[a] = { x: drag.startQuad[a]!.x + dx, y: drag.startQuad[a]!.y + dy };
    next[b] = { x: drag.startQuad[b]!.x + dx, y: drag.startQuad[b]!.y + dy };
    setQuad(next);
  };

  const endDrag = (event: React.PointerEvent) => {
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    setLoupe(null);
  };

  /** Ecken mit der Tastatur bewegen – ein Prozent pro Anschlag. */
  const nudge = (index: number, event: React.KeyboardEvent) => {
    const step = event.shiftKey ? 0.05 : 0.01;
    const deltas: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const delta = deltas[event.key];
    if (!delta) return;
    event.preventDefault();
    const next = [...quad] as Quad;
    const corner = quad[index]!;
    next[index] = {
      x: clamp(corner.x + delta[0], 0, 1),
      y: clamp(corner.y + delta[1], 0, 1),
    };
    setQuad(next);
  };

  /** Ein Blob als `<img>` laden und die alte Objekt-URL freigeben. */
  const keepUploaded = async (blob: Blob) => {
    if (uploadedUrlRef.current) URL.revokeObjectURL(uploadedUrlRef.current);
    const url = URL.createObjectURL(blob);
    uploadedUrlRef.current = url;
    try {
      const node = await new Promise<HTMLImageElement>((resolve, reject) => {
        const element = new Image();
        element.onload = () => resolve(element);
        element.onerror = () => reject(new Error('Bild ließ sich nicht laden'));
        element.src = url;
      });
      setUploadedImage(node);
    } catch {
      setUploadedImage(null);
    }
  };

  const upload = async (payload: File) => {
    setPhase('working');
    const outcome = await onUpload(payload);
    setUploaded(outcome.created[0] ?? null);
    // Ohne Zuschnitt ist das hochgeladene Bild die Aufnahme selbst – die liegt
    // schon geladen vor, ein zweites Mal einlesen wäre Verschwendung.
    setUploadedImage(image);
    setPhase(outcome.ok ? 'done' : 'edit');
  };

  const cropAndUpload = async () => {
    const source = sourceRef.current;
    if (!source || !file) return;
    setPhase('working');
    try {
      await afterPaint();
      const canvas = warpQuad(source, rotateQuad(quad, rotation));
      const blob = await canvasToBlob(canvas);
      const outcome = await onUpload(new File([blob], scanFilename(), { type: 'image/jpeg' }));
      setUploaded(outcome.created[0] ?? null);
      await keepUploaded(blob);
      setPhase(outcome.ok ? 'done' : 'edit');
    } catch {
      setPhase('edit');
    }
  };

  const result = sourceRef.current
    ? outputSize(sourceRef.current, rotateQuad(quad, rotation))
    : null;

  /**
   * Kann direkt benannt werden? Nur wenn ein Bild angekommen *und* die Bitmap
   * noch da ist. Bei einem Doppel („war schon im Archiv") gibt es kein neues
   * Medium – dann bleibt es bei der schlichten Bestätigung.
   */
  const canName = phase === 'done' && Boolean(uploaded) && Boolean(uploadedImage);

  const statusText: Record<Detection, string> = {
    pending: 'Ränder werden gesucht …',
    found: 'Ränder erkannt. Ecken bei Bedarf verschieben.',
    unsure: 'Ränder erkannt, aber nicht eindeutig – bitte die Ecken prüfen.',
    missed: 'Keine Ränder gefunden. Ecken bitte auf die Fotokanten ziehen.',
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Dunkel: hier wird ein Bildausschnitt beurteilt, und das geht auf
          hellem Papier nicht – die Fläche drumherum verschiebt den
          Helligkeitseindruck und zieht das Auge vom Bild weg. Es ist die
          einzige Ansicht der App, die ihre eigene Fläche mitbringt. */}
      <DialogContent
        size="lg"
        tone="ink"
        title={phase === 'done' ? 'Im Archiv angekommen' : 'Foto zuschneiden'}
        description={
          phase === 'done'
            ? undefined
            : 'Der Ausschnitt wird geradegerückt – ein schräg abfotografiertes Foto wird wieder rechteckig.'
        }
        footer={
          /* Mit Benennen-Schritt bringt der seine eigenen Knöpfe mit – zwei
             Fußzeilen übereinander wären eine zu viel. */
          canName ? undefined : phase === 'done' ? (
            <>
              <Button variant="ghost" className={GHOST_ON_INK} onClick={() => onOpenChange(false)}>
                Fertig
              </Button>
              {onNext ? (
                <Button variant="ghost" className={GOLD_ACTION} onClick={onNext}>
                  <Camera aria-hidden />
                  Nächstes Foto
                </Button>
              ) : null}
            </>
          ) : (
            <>
              {phase !== 'unreadable' && file ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className={GHOST_ON_INK}
                  disabled={phase === 'working'}
                  onClick={() => void upload(file)}
                  title="Lädt die Aufnahme unverändert hoch, mit allen Kameradaten"
                >
                  Ohne Zuschnitt
                </Button>
              ) : null}
              <Button variant="ghost" className={GHOST_ON_INK} onClick={() => onOpenChange(false)}>
                Abbrechen
              </Button>
              {/* Gold statt `primary`: `primary` ist im Hellen die Tinte, und
                  auf einer tintenfarbenen Tafel wäre der Knopf ein Loch. */}
              <Button
                variant="ghost"
                className={GOLD_ACTION}
                loading={phase === 'working'}
                disabled={phase !== 'edit' || !sourceRef.current}
                onClick={() => void cropAndUpload()}
              >
                <Crop aria-hidden />
                Zuschneiden &amp; hochladen
              </Button>
            </>
          )
        }
      >
        {phase === 'unreadable' ? (
          <p className="py-6 text-center text-sm text-panel-ink-2">
            Dieses Bildformat kann der Browser nicht anzeigen – ein Zuschnitt ist deshalb nicht
            möglich. Die Datei lässt sich aber unverändert hochladen.
          </p>
        ) : canName ? (
          /* Der eigentliche dritte und vierte Schritt: Gesichter erkennen,
             benennen, notfalls von Hand markieren. */
          <ScanFaces
            media={uploaded!}
            image={uploadedImage!}
            onClose={() => onOpenChange(false)}
            onNext={onNext}
          />
        ) : phase === 'done' ? (
          <div className="flex flex-col items-center gap-4 py-2">
            <div className="flex items-center gap-2.5 self-stretch rounded-xl bg-ok-soft px-3.5 py-3 text-[13.5px] font-semibold text-ok">
              <Check className="size-4 shrink-0" aria-hidden />
              Im Archiv angekommen.
            </div>

            {/* Der Abzug, leicht schief – er liegt jetzt im Album. */}
            {preview ? (
              <PhotoFrame size="lg" tilt className="max-w-64" caption="Gerade hochgeladen">
                <img src={preview} alt="Zugeschnittenes Foto" className="block w-full" />
              </PhotoFrame>
            ) : null}

            {/* Der nächste Handgriff steht *hier* und nicht im Fotoalbum:
                wer gerade ein Familienfoto abfotografiert hat, weiß in diesem
                Moment, wer darauf ist – zehn Bilder später nicht mehr. */}
            {uploaded ? (
              <div className="flex w-full max-w-sm flex-col items-center gap-2.5">
                <p className="text-center text-[12.5px] leading-relaxed text-panel-ink-2">
                  Wer ist darauf? Gesichter markieren und benennen geht jetzt am
                  schnellsten – später weiß es oft niemand mehr.
                </p>
                <Button asChild variant="ghost" className={cn(GOLD_ACTION, 'w-full')}>
                  <Link to={`/medien/${uploaded.id}`} onClick={() => onOpenChange(false)}>
                    <ScanFace aria-hidden />
                    Gesichter benennen
                  </Link>
                </Button>
              </div>
            ) : (
              <p className="max-w-sm text-center text-[12.5px] leading-relaxed text-panel-ink-2">
                Aufnahmedatum, Ort und wer darauf zu sehen ist, lassen sich später
                am Bild ergänzen – die Vorschaubilder entstehen gerade im
                Hintergrund.
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Der Hinweis ist die Anleitung für den nächsten Griff und trägt
                deshalb den Akzent – als grauer Kleintext hat ihn niemand
                gelesen und dann an den Ecken gezogen, ohne zu wissen, ob die
                Erkennung schon fertig war. */}
            <p className="mb-3 flex items-start gap-2.5 rounded-xl bg-accent/15 px-3.5 py-3 text-[13px] leading-snug text-panel-ink">
              {detection === 'pending' ? (
                <Spinner className="mt-px size-4 shrink-0 text-accent" />
              ) : (
                <ScanLine className="mt-px size-4 shrink-0 text-accent" aria-hidden />
              )}
              {statusText[detection]}
            </p>

            <div
              ref={frameRef}
              className="relative h-[44dvh] w-full touch-none overflow-hidden rounded-xl bg-panel-sunken select-none sm:h-[46vh]"
            >
              {image ? (
                <img
                  src={image.src}
                  alt="Aufnahme"
                  draggable={false}
                  className="pointer-events-none absolute"
                  style={
                    fit
                      ? { left: fit.left, top: fit.top, width: fit.width, height: fit.height }
                      : { inset: 0, width: '100%', height: '100%', objectFit: 'contain' }
                  }
                />
              ) : (
                <span className="absolute inset-0 flex items-center justify-center">
                  <Spinner className="size-6" />
                </span>
              )}

              {fit ? (
                <>
                  {/* Alles außerhalb des Vierecks abdunkeln, damit der Blick
                      am Ausschnitt hängt. */}
                  <svg
                    className="pointer-events-none absolute inset-0 size-full"
                    aria-hidden
                    viewBox={`0 0 ${box!.width} ${box!.height}`}
                  >
                    <defs>
                      <mask id="scan-mask">
                        <rect width={box!.width} height={box!.height} fill="white" />
                        <polygon
                          points={quad
                            .map(
                              (p) =>
                                `${fit.left + p.x * fit.width},${fit.top + p.y * fit.height}`,
                            )
                            .join(' ')}
                          fill="black"
                        />
                      </mask>
                    </defs>
                    <rect
                      width={box!.width}
                      height={box!.height}
                      fill="rgb(0 0 0 / 0.55)"
                      mask="url(#scan-mask)"
                    />
                    <polygon
                      points={quad
                        .map(
                          (p) => `${fit.left + p.x * fit.width},${fit.top + p.y * fit.height}`,
                        )
                        .join(' ')}
                      fill="none"
                      stroke="var(--accent)"
                      strokeWidth={2.5}
                    />
                  </svg>

                  {/* Seitenmitten: verschieben eine ganze Kante */}
                  {quad.map((corner, index) => {
                    const next = quad[(index + 1) % 4]!;
                    const mx = fit.left + ((corner.x + next.x) / 2) * fit.width;
                    const my = fit.top + ((corner.y + next.y) / 2) * fit.height;
                    return (
                      <button
                        key={`edge-${index}`}
                        type="button"
                        aria-label={`Kante ${index + 1} verschieben`}
                        onPointerDown={(event) => beginDrag(event, 'edge', index)}
                        onPointerMove={moveDrag}
                        onPointerUp={endDrag}
                        onPointerCancel={endDrag}
                        className="absolute size-9 -translate-x-1/2 -translate-y-1/2 touch-none rounded-full"
                        style={{ left: mx, top: my }}
                      >
                        <span className="absolute inset-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--accent)] ring-2 ring-[var(--accent)]/30" />
                      </button>
                    );
                  })}

                  {/* Ecken */}
                  {quad.map((corner, index) => (
                    <button
                      key={`corner-${index}`}
                      type="button"
                      aria-label={`Ecke ${index + 1} verschieben`}
                      onPointerDown={(event) => beginDrag(event, 'corner', index)}
                      onPointerMove={moveDrag}
                      onPointerUp={endDrag}
                      onPointerCancel={endDrag}
                      onKeyDown={(event) => nudge(index, event)}
                      className="absolute size-11 -translate-x-1/2 -translate-y-1/2 touch-none rounded-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
                      style={{
                        left: fit.left + corner.x * fit.width,
                        top: fit.top + corner.y * fit.height,
                      }}
                    >
                      {/* Goldener Punkt mit weichem Hof – so sitzt er auf
                          hellen *und* dunklen Bildstellen. Ein weißer Ring um
                          einen dunklen Punkt verschwand auf überbelichteten
                          Aufnahmen. */}
                      <span className="absolute inset-1/2 size-5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--accent)] ring-4 ring-[var(--accent)]/30" />
                    </button>
                  ))}

                  {/* Lupe: der Finger verdeckt die Ecke, die er gerade setzt. */}
                  {loupe && image ? (
                    <div
                      className="pointer-events-none absolute top-2 overflow-hidden rounded-full border-2 border-[var(--accent)] bg-panel-sunken shadow-pop"
                      style={{
                        width: LOUPE_SIZE,
                        height: LOUPE_SIZE,
                        // Auf die Seite ausweichen, auf der der Finger nicht ist.
                        left: loupe.x > 0.5 ? 8 : undefined,
                        right: loupe.x > 0.5 ? undefined : 8,
                      }}
                    >
                      <img
                        src={image.src}
                        alt=""
                        draggable={false}
                        className="absolute max-w-none"
                        style={{
                          width: fit.width * LOUPE_ZOOM,
                          height: fit.height * LOUPE_ZOOM,
                          left: LOUPE_SIZE / 2 - loupe.x * fit.width * LOUPE_ZOOM,
                          top: LOUPE_SIZE / 2 - loupe.y * fit.height * LOUPE_ZOOM,
                        }}
                      />
                      <span className="absolute top-1/2 left-0 h-px w-full bg-[var(--accent)]/80" />
                      <span className="absolute top-0 left-1/2 h-full w-px bg-[var(--accent)]/80" />
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>

            {/* Auf dem Handy drei gleich breite Knöpfe in einer Reihe, ab `sm`
                die kompakte Werkzeugzeile. */}
            <div className="mt-3 grid grid-cols-3 items-center gap-2 sm:flex sm:flex-wrap">
              <Button
                variant="ghost"
                size="sm"
                className={TOOL_ON_INK}
                disabled={!image || detection === 'pending'}
                onClick={() => image && detect(image)}
              >
                <Wand2 aria-hidden />
                Automatisch
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={TOOL_ON_INK}
                disabled={!image}
                onClick={() => {
                  setQuad(FULL_QUAD);
                  setDetection('missed');
                }}
              >
                <Maximize2 aria-hidden />
                Ganzes Bild
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className={TOOL_ON_INK}
                disabled={!image}
                onClick={() => setRotation((value) => (value + 1) % 4)}
                title="Ergebnis um 90° drehen"
              >
                <RotateCw aria-hidden />
                Drehen
              </Button>

              <div className="col-span-3 flex items-center justify-between gap-2 sm:ml-auto sm:justify-end">
                <span className="font-mono text-[11px] text-panel-ink-2 tabular">
                  {result ? `${result.width} × ${result.height} px` : ''}
                </span>
                <span
                  className={cn(
                    'flex size-11 items-center justify-center overflow-hidden rounded-[3px] bg-[var(--photo-paper)] p-0.5',
                    !preview && 'bg-panel-line',
                  )}
                  title="Vorschau des Ergebnisses"
                >
                  {preview ? (
                    <img src={preview} alt="Vorschau des Zuschnitts" className="max-h-full max-w-full" />
                  ) : (
                    <Spinner className="size-4" />
                  )}
                </span>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
